import { useEffect, useId, useRef } from "react";
import type { ThemeClasses } from "../../types/components";
import { panelThemeClasses } from "../../theme/themeClasses";
import { CRUDTooltip } from "./CRUDTooltip";
import { Button } from "../ui/Button";

// Modales abiertos, del más viejo al más reciente: sólo el de arriba atiende
// Escape y la trampa de foco (un modal sobre otro no cierra a los dos).
const openModals: HTMLElement[] = [];
const isTopModal = (panel: HTMLElement) => openModals[openModals.length - 1] === panel;

/** Contenedores de diálogos ajenos que pueden abrirse encima de este modal. */
const FOREIGN_DIALOG = ".swal2-container,[role='dialog'],[role='listbox']";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest("[aria-hidden='true']") && el.offsetParent !== null,
  );
}

export default function Modal({
    content,
    themeClasses = panelThemeClasses(),
    onClose,
    title = "Ejemplo de titulo",
    buttonAction,
    showEditButton = false,
    onEdit,
    width,
    disableCRUD = false,
}: {
    content: React.ReactNode;
    themeClasses?: ThemeClasses;
    onClose: () => void;
    title: string;
    buttonAction?: React.ReactNode;
    showEditButton?: boolean;
    onEdit?: () => void;
    width?: string;
    disableCRUD?: boolean;
}) {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    // `onClose` puede cambiar en cada render; el listener lee siempre el último.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Accesibilidad (CC-CONSOLE#1639): foco inicial en el primer control del
    // contenido (si no hay, el panel), Tab/Shift+Tab se quedan dentro, Escape
    // cierra y al cerrar el foco vuelve a donde estaba.
    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const previous = document.activeElement as HTMLElement | null;
        openModals.push(panel);
        const inBody = bodyRef.current ? focusables(bodyRef.current) : [];
        (inBody[0] ?? panel).focus({ preventScroll: true });

        const onKeyDown = (event: KeyboardEvent) => {
            if (!isTopModal(panel)) return;
            // Un diálogo ajeno encima (SweetAlert de confirmDialog, portal de
            // Combobox/listbox) se atiende solo: ni le robamos el foco ni
            // cerramos este modal con su Escape.
            const target = event.target instanceof Element ? event.target : null;
            const foreignDialog = target && !panel.contains(target) ? target.closest(FOREIGN_DIALOG) : null;
            if (foreignDialog && foreignDialog !== panel) return;
            if (event.key === "Escape") {
                if (document.querySelector(".swal2-container")) return;
                event.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (event.key !== "Tab") return;
            const items = focusables(panel);
            if (!items.length) {
                event.preventDefault();
                panel.focus();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            const inside = active instanceof Node && panel.contains(active);
            if (event.shiftKey && (!inside || active === first || active === panel)) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && (!inside || active === last)) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            const at = openModals.indexOf(panel);
            if (at >= 0) openModals.splice(at, 1);
            if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus({ preventScroll: true });
        };
    }, []);

    return (
        // z-[70]: por encima del sidebar de Lite (relative z-[60]), que antes
        // tapaba cualquier modal abierto desde el mapa. Debajo del portal del
        // Combobox y del preview de imagenes (z-[80]+), que deben seguir arriba.
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4 max-sm:items-end max-sm:p-3 max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={`${themeClasses.bg} rounded-lg ${width ? width : 'max-w-2xl'} max-h-[95dvh] max-sm:max-h-[calc(100dvh-1.5rem)] max-sm:!w-full max-sm:!max-w-full max-sm:rounded-2xl overflow-y-auto overflow-x-hidden border ${themeClasses.border} shadow-2xl outline-none`}
            >
                <div className="px-4 sm:px-6 py-3.5 border-b border-line">
                    <div className="flex items-center justify-between gap-4">
                        <h2 id={titleId} className={`text-base font-semibold ${themeClasses.text}`}>{title}</h2>
                        <div className="flex items-center gap-2">
                            {showEditButton && (
                                <CRUDTooltip>
                                    <Button size="sm" onClick={onEdit} disabled={disableCRUD}>
                                        Editar
                                    </Button>
                                </CRUDTooltip>
                            )}

                            {buttonAction && buttonAction}
                            <Button
                                variant="ghost"
                                size="sm"
                                iconOnly
                                onClick={onClose}
                                aria-label="Cerrar"
                                icon={<i className="ri-close-line text-lg" />}
                            />
                        </div>
                    </div>
                </div>
                <div ref={bodyRef} className="px-4 sm:px-6 py-5 overflow-y-auto">{content}</div>
            </div>
        </div>
    );
}
